import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import {
  findUpperRecursive,
  genRecursiveOptions,
  getRecursiveArray,
} from '@/utils';
import { z } from 'zod';
import {
  ExpenditurePurposeFormSchema,
  ExpenditurePurposesModel,
  type UpdateExpenditurePurposeBudgetAgendaSortOrderParamsSchema,
  UpdateExpenditurePurposeParamsSchema,
} from '../models';
import { expenditurePurposeNameWithCode } from '../utils';

const ExpenditurePurposesModelElement = ExpenditurePurposesModel.element;

type ExpenditurePurposesParams = {
  params?: { isMajorMatter?: boolean; showMigrated?: true };
};
type ExpenditurePurposesArgs<T> = {
  select?: (data: z.infer<typeof ExpenditurePurposesModel>) => T;
  enabled?: boolean;
} & ExpenditurePurposesParams;

export const useExpenditurePurposes = <
  T = Array<z.infer<typeof ExpenditurePurposesModelElement>>,
>(
  { select, enabled, params }: ExpenditurePurposesArgs<T> = {
    params: { isMajorMatter: false },
  },
) => {
  const { showMigrated, ...restParams } = params ?? {};
  return useQuery({
    queryKey: ['expenditurePurposes', params],
    queryFn: async () => {
      const expenditurePurposes = await axiosGet('api/expenditure-purposes/', {
        ...restParams,
        ...(!showMigrated && { isMigrated: false }),
      });
      return ExpenditurePurposesModel.parse(expenditurePurposes);
    },
    select,
    enabled,
  });
};

export const useExpenditurePurposeRecursive = (
  id: string,
  showMigrated?: true,
) => {
  return useExpenditurePurposes({
    params: { showMigrated },
    select: (data) => findUpperRecursive(id, data, []).reverse(),
    enabled: !!id,
  });
};

type OptionParams = {
  upperLevel?: number;
  accountingId?: string;
  showCode?: boolean;
} & ExpenditurePurposesParams;
export const useExpenditurePurposeOptions = ({
  upperLevel,
  accountingId,
  showCode,
  params,
}: OptionParams = {}) => {
  return useExpenditurePurposes({
    params,
    select: (data) => {
      const recursiveArray = getRecursiveArray(
        data.flatMap((d) =>
          !d.upperId
            ? showCode
              ? {
                  ...d,
                  name: expenditurePurposeNameWithCode({
                    code: d.code,
                    level: d.level,
                    isMajorMatter: !!d.isMajorMatter,
                    name: d.name,
                  }),
                }
              : d
            : [],
        ),
        data.flatMap((d) =>
          !upperLevel || d.level <= upperLevel
            ? showCode
              ? {
                  ...d,
                  name: expenditurePurposeNameWithCode({
                    code: d.code,
                    level: d.level,
                    isMajorMatter: !!d.isMajorMatter,
                    name: d.name,
                  }),
                }
              : d
            : [],
        ),
      );

      const filteredArray = recursiveArray.filter(
        (data) => !accountingId || data.accountingId === accountingId,
      );

      return genRecursiveOptions(filteredArray, 'id', 'name');
    },
  });
};

type FormValues = z.infer<typeof ExpenditurePurposeFormSchema>;

export const useCreateExpenditurePurpose = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { upperIds, period, ...parsed } =
        ExpenditurePurposeFormSchema.parse(values);
      const res = await axiosPost('/api/expenditure-purposes/', {
        ...parsed,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
        upperId: upperIds?.at(-1),
      });
      await invalidate(['expenditurePurposes']);
      return res;
    },
  });
};

export const useUpdateExpenditurePurpose = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, upperIds, period, ...payload } =
        UpdateExpenditurePurposeParamsSchema.extend({
          id: z.string().uuid(),
        }).parse(values);
      const res = await axiosPatch(`/api/expenditure-purposes/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
        upperId: upperIds?.at(-1),
      });
      await invalidate(['expenditurePurposes']);
      return res;
    },
  });
};

export const useDeleteExpenditurePurpose = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/expenditure-purposes/${id}/`);
      await invalidate(['expenditurePurposes']);
      return res;
    },
  });
};

export const useChangeBudgetAgendaOrderOfExpenditurePurposes = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<
        typeof UpdateExpenditurePurposeBudgetAgendaSortOrderParamsSchema
      >,
    ) => {
      const res = await axiosPost(
        '/api/expenditure-purposes/update-budget-agenda-sort-order/',
        params,
      );
      await invalidate(['expenditurePurposes']);
      return res;
    },
  });
};
